import { useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useForm } from '@tanstack/react-form'
import { useSelector } from '@tanstack/react-store'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { NumberInput } from '@/components/ui/number-input'
import {
  Users,
  Shield,
  Building,
  Flag,
  Info,
  Wallet,
  AlertTriangle,
  List,
  Plus,
  Trash2,
  Link,
  X,
  Check,
  Loader2,
} from 'lucide-react'
import {
  StatsBar,
  StatsBarId,
  StatsBarActivity,
} from '../../components/StatsBar'
import { AuthService } from '@/services/core/auth'
import { ApiError, apiErrorDescription } from '@/services/core/apiError'
import { PoliticalCampaignsApi, PoliticalCampaignInput } from '@/services/political/politicalCampaignsApi'
import { PoliticalEntitiesApi } from '@/services/political/politicalEntitiesApi'
import { ElectionsApi } from '@/services/political/electionsApi'
import { PoliticalCampaign, PoliticalCampaignTopic, PoliticalEntity, ExternalApproval } from '../../types/models'
import { InlineEditTitle } from '../../components/InlineEditTitle'
import { AppShell } from '../../components/AppShell'
import { FormSkeleton } from '../../components/FormSkeleton'
import { useUnsavedChanges } from '../../hooks/useUnsavedChanges'
import { UnsavedChangesDialog } from '../../components/UnsavedChangesDialog'
import { NetworkObjectLookup } from '../../components/NetworkObjectLookup'
import { NewPayerModal } from '../../components/NewPayerModal'
import { useCurrentUser } from '../../contexts/UserContext'
import { useNotifications } from '../../contexts/NotificationsContext'
import { Card, CardAction, CardHeader, CardIcon, CardTitle, CardContent } from '@/components/ui/card'
import { CampaignTransparencyCard } from '@/components/political/CampaignTransparency'
import { ObjectTypeLabel } from '../../components/ObjectTypeLabel'
import { formatNumber, formatDate } from '../../utils/format'
import { ValidationWarnings } from '@/components/ValidationWarnings'
import { ObjectStateToggle, useSaveObjectState, type ObjectState } from '../../components/ObjectStateToggle'
import { VersionHistoryButton } from '@/components/VersionHistoryButton'

const CREATE_NEW_PAYER = '__CREATE_NEW_PAYER__'

const CURRENCIES = ['NOK', 'EUR', 'USD', 'GBP', 'SEK', 'DKK']
const TOPIC_COLORS = [
  'var(--ad-color-1)',
  'var(--ad-color-2)',
  'var(--ad-color-3)',
  'var(--ad-color-4)',
  'var(--ad-color-5)',
  'var(--ad-color-6)',
  'var(--ad-color-7)',
  'var(--ad-color-8)',
]

// ── Inner form component ────────────────────────────────────────────────────

interface PoliticalCampaignEditFormProps {
  campaign: PoliticalCampaign | null
  id: string
  isNew: boolean
  initialElectionId?: string
}

function PoliticalCampaignEditForm({ campaign, id, isNew, initialElectionId }: PoliticalCampaignEditFormProps) {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const hasToken = AuthService.isAuthenticated()
  const { t } = useTranslation()

  const [topicsError, setTopicsError] = useState(false)
  const [payerModalOpen, setPayerModalOpen] = useState(false)
  const [payerModalTopicIndex, setPayerModalTopicIndex] = useState<number | null>(null)

  const form = useForm({
    defaultValues: {
      name: campaign?.name ?? '',
      description: campaign?.description ?? '',
      politicalEntityId: campaign?.politicalEntityId ?? '',
      electionId: campaign?.electionId ?? initialElectionId ?? '',
      totalBudget: campaign ? String(campaign.totalBudget) : '',
      currency: campaign?.currency ?? 'NOK',
      topics: campaign?.topics ?? [] as PoliticalCampaignTopic[],
    },
    onSubmit: ({ value }) => {
      if (value.topics.length === 0) {
        setTopicsError(true)
        return
      }
      setTopicsError(false)
      mutation.mutate({
        name: value.name,
        description: value.description,
        politicalEntityId: value.politicalEntityId,
        electionId: value.electionId,
        totalBudget: Number(value.totalBudget),
        currency: value.currency,
        topics: value.topics,
        objectState: (campaign?.objectState ?? 'ACTIVE') as ObjectState,
      })
    },
  })
  const saveObjectState = useSaveObjectState(
    (objectState) => PoliticalCampaignsApi.update(id!, {
      name: campaign!.name,
      description: campaign!.description,
      politicalEntityId: campaign!.politicalEntityId,
      electionId: campaign!.electionId,
      totalBudget: campaign!.totalBudget,
      currency: campaign!.currency,
      topics: campaign!.topics,
      objectState,
    }),
    ['politicalCampaign', id],
    ['politicalCampaigns']
  )
  const { isDirty, markClean, blocker } = useUnsavedChanges(form)

  const { data: entities = [] } = useQuery({
    queryKey: ['politicalEntities'],
    queryFn: () => PoliticalEntitiesApi.getAll(),
    enabled: hasToken,
    staleTime: 5 * 60 * 1000,
  })
  const sponsors = entities.filter((e) => e.type === 'SPONSOR')
  const payers = entities.filter((e) => e.type === 'PAYER')
  const payerOptions = [
    ...payers,
    { id: CREATE_NEW_PAYER, name: t('political.createNewPayer') },
  ]

  const politicalEntityId = useSelector(form.store, (s) => s.values.politicalEntityId)
  const controllerEntity = entities.find(
    (e) =>
      e.type === 'CONTROLLER' && e.id === sponsors.find((s) => s.id === politicalEntityId)?.parentId
  )

  const { data: elections = [] } = useQuery({
    queryKey: ['elections'],
    queryFn: ElectionsApi.getAll,
    enabled: hasToken,
  })

  const mutation = useMutation({
    mutationFn: (input: PoliticalCampaignInput) =>
      isNew ? PoliticalCampaignsApi.create(id!, input) : PoliticalCampaignsApi.update(id!, input),
    onSuccess: (data) => {
      queryClient.setQueryData(['politicalCampaign', id], data)
      queryClient.setQueryData(['politicalCampaigns'], (old: PoliticalCampaign[] | undefined) => {
        if (!old) return [data]
        const idx = old.findIndex((c) => c.id === data.id)
        return idx >= 0 ? old.map((c) => (c.id === data.id ? data : c)) : [...old, data]
      })
      markClean()
      if (isNew) {
        navigate(`/political/campaigns/${id}`, { replace: true })
      }
      toast.success(t('common.saved'), {
        description: isNew ? t('political.savedSuccess') : t('political.savedEditSuccess'),
        duration: 3000,
      })
    },
    onError: (error) => {
      const detail =
        apiErrorDescription(error)
      toast.error(
        error instanceof ApiError ? error.message : t('political.saveFailed'),
        { description: detail, duration: 5000 }
      )
    },
  })

  // An advertiser can be linked to several campaigns, so list every invite
  // here, not just those still unlinked everywhere.
  const { allNotifications: notifications, refresh: refreshNotifications } = useNotifications()

  const linkMutation = useMutation({
    mutationFn: (input: PoliticalCampaignInput) => PoliticalCampaignsApi.update(id!, input),
    onSuccess: (data) => {
      queryClient.setQueryData(['politicalCampaign', id], data)
      queryClient.setQueryData(['politicalCampaigns'], (old: PoliticalCampaign[] | undefined) => {
        if (!old) return [data]
        const idx = old.findIndex((c) => c.id === data.id)
        return idx >= 0 ? old.map((c) => (c.id === data.id ? data : c)) : [...old, data]
      })
      refreshNotifications()
    },
    onError: (error) => {
      const detail =
        apiErrorDescription(error)
      toast.error(
        error instanceof ApiError ? error.message : t('political.linkFailed'),
        { description: detail, duration: 5000 }
      )
    },
  })

  const handleLink = (approval: ExternalApproval) => {
    if (!campaign) return
    const existing = campaign.externalApprovals ?? []
    const updated = [...existing, approval]
    linkMutation.mutate({
      name: campaign.name,
      description: campaign.description,
      politicalEntityId: campaign.politicalEntityId,
      electionId: campaign.electionId,
      totalBudget: campaign.totalBudget,
      currency: campaign.currency,
      topics: campaign.topics,
      objectState: campaign.objectState,
      externalApprovals: updated,
    }, {
      onSuccess: (data) => {
        // The backend drops a link it does not allow rather than failing the
        // save — a team link request only covers campaigns of the organisation
        // it was sent to.
        if (!(data.externalApprovals ?? []).some((a) => a.id === approval.id)) {
          toast.error(t('political.linkFailed'), {
            description: t('political.linkRejected', { name: approval.name }),
            duration: 8000,
          })
          return
        }
        toast.success(t('common.saved'), {
          description: t('political.linkSuccess'),
          duration: 3000,
        })
      },
    })
  }

  const handleRevoke = (publicId: string) => {
    if (!campaign) return
    const existing = campaign.externalApprovals ?? []
    const updated = existing.filter((a) => a.id !== publicId)
    linkMutation.mutate({
      name: campaign.name,
      description: campaign.description,
      politicalEntityId: campaign.politicalEntityId,
      electionId: campaign.electionId,
      totalBudget: campaign.totalBudget,
      currency: campaign.currency,
      topics: campaign.topics,
      objectState: campaign.objectState,
      externalApprovals: updated,
    }, {
      onSuccess: () => {
        toast.success(t('common.saved'), {
          description: t('political.unlinkSuccess'),
          duration: 3000,
        })
      },
    })
  }

  const linkedIds = new Set((campaign?.externalApprovals ?? []).map((a) => a.id))
  const currentUser = useCurrentUser()
  const isController = !!currentUser?.isController

  const addTopic = () => {
    setTopicsError(false)
    const currency = form.getFieldValue('currency')
    const topics = form.getFieldValue('topics')
    form.setFieldValue('topics', [
      ...topics,
      { id: `topic-${crypto.randomUUID().replace(/-/g, '').slice(0, 12)}`, name: '', amount: 0, currency },
    ])
  }

  const updateTopic = (
    index: number,
    field: keyof PoliticalCampaignTopic,
    value: string | number
  ) => {
    const topics = form.getFieldValue('topics')
    form.setFieldValue('topics', topics.map((t, i) => (i === index ? { ...t, [field]: value } : t)))
  }

  const removeTopic = (index: number) => {
    const topics = form.getFieldValue('topics')
    form.setFieldValue('topics', topics.filter((_, i) => i !== index))
  }

  const handlePayerChange = (topicIndex: number, value: string) => {
    if (value === CREATE_NEW_PAYER) {
      setPayerModalTopicIndex(topicIndex)
      setPayerModalOpen(true)
    } else {
      updateTopic(topicIndex, 'payerId', value ?? '')
    }
  }

  const handlePayerCreated = (entity: PoliticalEntity) => {
    if (payerModalTopicIndex !== null) {
      updateTopic(payerModalTopicIndex, 'payerId', entity.id)
    }
    setPayerModalOpen(false)
    setPayerModalTopicIndex(null)
  }

  const crumbs = [
    { label: t('political.campaigns'), href: '/political/campaigns' },
    { label: isNew ? t('political.newCampaign') : (campaign?.name ?? t('political.editCampaign')) },
  ]

  const topics = useSelector(form.store, (s) => s.values.topics)
  const currency = useSelector(form.store, (s) => s.values.currency)
  const totalBudget = useSelector(form.store, (s) => s.values.totalBudget)
  const totalTopicBudget = topics.reduce((sum, t) => sum + (t.amount || 0), 0)
  const budget = Number(totalBudget) || 0
  const remaining = Math.max(0, budget - totalTopicBudget)
  const overBudget = budget > 0 && totalTopicBudget > budget

  const fmt = (n: number) =>
    formatNumber(n, { style: 'currency', currency, maximumFractionDigits: 0 })

  const meterItems = [
    ...topics
      .filter((t) => (t.amount || 0) > 0)
      .map((t, i) => ({
        label: t.name || 'Unnamed',
        value: t.amount,
        color: TOPIC_COLORS[i % TOPIC_COLORS.length],
      })),
    ...(remaining > 0 ? [{ label: 'Unallocated', value: remaining, color: 'var(--ad-border)' }] : []),
  ]

  return (
    <AppShell crumbs={crumbs} showBack>
      <form className="ad-page" onSubmit={(e) => { e.preventDefault(); form.handleSubmit() }}>
        <div className="ad-page__header">
          <div className="ad-page__title-block">
            <ObjectTypeLabel type={t('objectTypes.politicalCampaign')} />
            <h1>
              <form.Field
                name="name"
                validators={{
                  onChange: ({ value }) => (value.trim() ? undefined : t('common.nameRequired')),
                  onSubmit: ({ value }) => (value.trim() ? undefined : t('common.nameRequired')),
                }}
              >
                {(field) => (
                  <InlineEditTitle
                    value={field.state.value}
                    onChange={(v) => field.handleChange(v)}
                    placeholder={t('political.campaignNamePlaceholder')}
                    required
                    error={field.state.meta.errors[0] as string | undefined}
                  />
                )}
              </form.Field>
            </h1>
            {isNew && <p>{t('political.campaignsSubtitle')}</p>}
          </div>
          {campaign && !isNew && (
            <div className="ad-page__actions">
              <VersionHistoryButton />
              <ObjectStateToggle value={campaign!.objectState} onChange={saveObjectState} />
            </div>
          )}
        </div>

        {campaign && !isNew && (
          <StatsBar
            items={[
              {
                label: t('common.id'),
                content: <StatsBarId value={campaign.id} />,
              },
              {
                label: t('stats.activity'),
                content: (
                  <StatsBarActivity
                    created={formatDate(campaign.createTime)}
                    updated={formatDate(campaign.updateTime)}
                  />
                ),
              },
            ]}
          />
        )}

        <ValidationWarnings warnings={campaign?.validationWarnings} />

        {/* Sponsor / Controller / Election card */}
        <Card tone="attention" className={!isNew ? 'opacity-60 hover:opacity-100 transition-opacity duration-200' : undefined}>
          <CardHeader>
            <CardTitle>
              <CardIcon icon={Users} />
              {t('political.politicalContext')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {isNew ? (
              <div className="field-grid field-grid--3">
                <div className="p-field">
                  <label><Shield className="size-3.5" style={{ marginRight: 6, display: 'inline' }} />{t('political.controller')}</label>
                  <span className="ad-detail-item__value" style={{ minHeight: 36, fontSize: 13.5, color: controllerEntity ? 'var(--ad-text)' : 'var(--ad-text-muted)' }}>
                    {controllerEntity?.name ?? t('political.selectSponsor')}
                  </span>
                </div>
                <div className="p-field">
                  <label><Building className="size-3.5" style={{ marginRight: 6, display: 'inline' }} />{t('political.sponsor')}</label>
                  <form.Field name="politicalEntityId">
                    {(field) => (
                      <Select label={t('political.sponsor')} value={field.state.value} onValueChange={(v) => field.handleChange(v ?? '')}>
                        <SelectTrigger><SelectValue placeholder={t('political.sponsorPlaceholder')}>{sponsors.find((o) => o.id === field.state.value)?.name ?? null}</SelectValue></SelectTrigger>
                        <SelectContent>
                          {sponsors.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    )}
                  </form.Field>
                </div>
                <div className="p-field">
                  <label><Flag className="size-3.5" style={{ marginRight: 6, display: 'inline' }} />{t('political.election')}</label>
                  <form.Field name="electionId">
                    {(field) => (
                      <Select label={t('political.election')} value={field.state.value} onValueChange={(v) => field.handleChange(v ?? '')}>
                        <SelectTrigger><SelectValue placeholder={t('political.electionPlaceholder')}>{elections.find((o) => o.id === field.state.value)?.name ?? null}</SelectValue></SelectTrigger>
                        <SelectContent>
                          {elections.map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    )}
                  </form.Field>
                </div>
              </div>
            ) : (
              <div className="ad-detail-row">
                <div className="ad-detail-item">
                  <span className="ad-detail-item__label">{t('political.controller')}</span>
                  <span className="ad-detail-item__value">
                    <Shield className="size-4" />
                    {controllerEntity?.name ?? '—'}
                  </span>
                </div>
                <div className="ad-detail-item">
                  <span className="ad-detail-item__label">{t('political.sponsor')}</span>
                  <span className="ad-detail-item__value">
                    <Building className="size-4" />
                    {sponsors.find((s) => s.id === politicalEntityId)?.name ?? '—'}
                  </span>
                </div>
                <div className="ad-detail-item">
                  <span className="ad-detail-item__label">{t('political.election')}</span>
                  <span className="ad-detail-item__value">
                    <Flag className="size-4" />
                    {elections.find((e) => e.id === form.getFieldValue('electionId'))?.name ?? '—'}
                  </span>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        <div className="card-row">
          {/* Name & Description card */}
          <Card className="flex-1 min-w-0">
            <CardHeader>
              <CardTitle>
                <CardIcon icon={Info} />
                {t('political.campaignDetails')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="field-grid">
                <div className="p-field full">
                  <label>{t('common.description')}</label>
                  <form.Field name="description">
                    {(field) => (
                      <Textarea
                        value={field.state.value}
                        onChange={(e) => field.handleChange(e.target.value)}
                        placeholder={t('advertisers.descriptionPlaceholder')}
                        rows={2}
                        style={{ width: '100%', resize: 'vertical' }}
                      />
                    )}
                  </form.Field>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Budget card */}
          <Card tone="money" className="flex-1 min-w-0">
            <CardHeader>
              <CardTitle>
                <CardIcon icon={Wallet} />
                {t('political.budget')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="field-grid">
                <div className="p-field">
                  <label>{t('political.totalBudget')}</label>
                  <form.Field name="totalBudget">
                    {(field) => (
                      <NumberInput
                        value={field.state.value ? Number(field.state.value) : null}
                        onChange={(v) => field.handleChange(v != null ? String(v) : '')}
                        min={0}
                        placeholder="0"
                        required
                      />
                    )}
                  </form.Field>
                </div>
                <div className="p-field">
                  <label>{t('rulesets.currency')}</label>
                  <form.Field name="currency">
                    {(field) => (
                      <Select label={t('rulesets.currency')} value={field.state.value} onValueChange={(v) => field.handleChange(v ?? '')}>
                        <SelectTrigger><SelectValue>{field.state.value || null}</SelectValue></SelectTrigger>
                        <SelectContent>
                          {CURRENCIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    )}
                  </form.Field>
                </div>
              </div>

              {budget > 0 && meterItems.length > 0 && (
                <div className="mt-4 pt-4 border-t border-(--border)">
                  {overBudget && (
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 6,
                        marginBottom: 10,
                        fontSize: 12.5,
                        color: 'var(--ad-danger)',
                        fontWeight: 600,
                      }}
                    >
                      <AlertTriangle className="size-3.5" style={{ flexShrink: 0 }} />
                      {t('political.budgetExceeded', { amount: fmt(totalTopicBudget - budget) })}
                    </div>
                  )}
                  {(() => {
                    const meterMax = overBudget ? totalTopicBudget : budget
                    return (
                      <div>
                        <div className="flex h-2 w-full overflow-hidden rounded-full bg-(--muted)">
                          {meterItems.map((m, i) => (
                            <div key={i} style={{ width: `${(m.value / meterMax) * 100}%`, background: m.color }} className="h-full transition-all" />
                          ))}
                        </div>
                        <div className="flex flex-wrap gap-3 mt-2">
                          {meterItems.map((m, i) => (
                            <span key={i} className="flex items-center gap-1.5 text-xs text-(--muted-foreground)">
                              <span className="size-2 rounded-full" style={{ background: m.color }} />
                              {m.label}: {fmt(m.value)}
                            </span>
                          ))}
                        </div>
                      </div>
                    )
                  })()}
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      marginTop: 10,
                      fontSize: 12,
                      color: 'var(--ad-text-muted)',
                    }}
                  >
                    <span>
                      {t('political.allocated')}{' '}
                      <strong style={{ color: 'var(--ad-text)' }}>{fmt(totalTopicBudget)}</strong>
                    </span>
                    <span>
                      {t('political.totalBudgetLabel')}{' '}
                      <strong style={{ color: 'var(--ad-text)' }}>{fmt(budget)}</strong>
                    </span>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        <div className="card-row">
          {/* Topics card */}
          <Card tone="attention" className={`flex-1 min-w-0${(topicsError || overBudget) ? ' border-(--ad-danger) shadow-[0_0_0_3px_rgba(220,38,38,0.1)]' : ''}`}>
            <CardHeader>
              <div>
                <CardTitle>
                  <CardIcon icon={List} />
                  {t('political.topics')}
                  {topics.length > 0 && (
                    <span
                      style={{
                        fontSize: 11,
                        fontWeight: 600,
                        color: 'var(--ad-text-muted)',
                        background: 'var(--ad-neutral-soft)',
                        padding: '1px 8px',
                        borderRadius: 999,
                        letterSpacing: '0.04em',
                        textTransform: 'uppercase',
                      }}
                    >
                      {topics.length}
                    </span>
                  )}
                </CardTitle>
                <div className="ad-card__subtitle">{t('political.budgetAllocations')}</div>
              </div>
              <CardAction>
                <Button type="button" size="sm" onClick={addTopic}>
                  <Plus className="size-4" />
                  {t('political.addTopic')}
                </Button>
              </CardAction>
            </CardHeader>

            {topics.length === 0 ? (
              <div className="empty-state" style={{ padding: '32px 24px' }}>
                <List className={`size-8${topicsError ? ' empty-state__icon--error' : ''}`} />
                <div className="empty-state__title">
                  {topicsError ? t('political.topicRequired') : t('political.noTopicsYet')}
                </div>
                <div className="empty-state__body">
                  {topicsError ? t('political.topicRequiredBody') : t('political.noTopicsBody')}
                </div>
              </div>
            ) : (
              <>
                <CardContent className="p-0">
                  <div className="overflow-x-auto">
                    <table className="ad-table" style={{ minWidth: 600 }}>
                      <thead>
                        <tr>
                          <th style={{ paddingLeft: 20 }}>{t('political.topicName')}</th>
                          <th style={{ width: 280 }}>{t('political.payer')}</th>
                          <th style={{ width: 200 }}>{t('political.topicAmount', { currency })}</th>
                          <th style={{ width: 52 }} />
                        </tr>
                      </thead>
                      <tbody>
                        {topics.map((topic, i) => (
                          <tr key={topic.id}>
                            <td style={{ paddingLeft: 20 }}>
                              <Input
                                value={topic.name}
                                onChange={(e) => updateTopic(i, 'name', e.target.value)}
                                placeholder={t('political.topicNamePlaceholder')}
                                style={{ width: '100%' }}
                              />
                            </td>
                            <td>
                              <Select value={topic.payerId ?? undefined} onValueChange={(v) => handlePayerChange(i, v === '__clear__' ? '' : (v ?? ''))}>
                                <SelectTrigger><SelectValue placeholder={t('political.payerPlaceholder')}>{payerOptions.find((o) => o.id === topic.payerId)?.name ?? null}</SelectValue></SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="__clear__">—</SelectItem>
                                  {payerOptions.map((opt) => (
                                    <SelectItem key={opt.id} value={opt.id}>
                                      {opt.id === CREATE_NEW_PAYER
                                        ? <span style={{ color: 'var(--ad-primary)', fontWeight: 600 }}><Plus className="size-3" style={{ marginRight: 6, display: 'inline' }} />{opt.name}</span>
                                        : opt.name}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </td>
                            <td>
                              <NumberInput
                                value={topic.amount || null}
                                onChange={(v) => updateTopic(i, 'amount', v ?? 0)}
                                min={0}
                                placeholder="0"
                              />
                            </td>
                            <td>
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                onClick={() => removeTopic(i)}
                                title={t('political.removeTopic')}
                              >
                                <Trash2 className="size-4" />
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </>
            )}
          </Card>
        </div>

        {!isNew && (
          <div className="card-row">
            {campaign && <CampaignTransparencyCard campaign={campaign} className="flex-1 min-w-0" />}

            {/* Cross-network responses */}
            <Card tone="delivery" className="flex-1 min-w-0">
              <CardHeader>
                <div>
                  <CardTitle>
                    <CardIcon icon={Link} />
                    {t('political.crossNetwork')}
                  </CardTitle>
                  <div className="ad-card__subtitle">{t('political.crossNetworkSubtitle')}</div>
                </div>
              </CardHeader>

              {notifications.length === 0 && (campaign?.externalApprovals ?? []).length === 0 ? (
                <div className="empty-state" style={{ padding: '32px 24px' }}>
                  <Link className="size-8" />
                  <div className="empty-state__title">{t('political.noCrossNetwork')}</div>
                  <div className="empty-state__body">{t('political.noCrossNetworkBody')}</div>
                </div>
              ) : (
                <CardContent className="p-0">
                  <table className="ad-table">
                    <thead>
                      <tr>
                        <th style={{ paddingLeft: 20 }}>{t('common.name')}</th>
                        <th>{t('common.description')}</th>
                        <th style={{ width: 120 }}>{t('common.status')}</th>
                        {!isController && <th style={{ width: 120 }} />}
                      </tr>
                    </thead>
                    <tbody>
                      {notifications.map((n) => {
                        const publicId = n.advertiser?.publicId
                        const isLinked = !!publicId && linkedIds.has(publicId)
                        return (
                          <tr key={n.id}>
                            <td style={{ paddingLeft: 20 }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <Building className="size-3.5" style={{ color: 'var(--ad-text-muted)' }} />
                                {n.advertiser?.name ?? n.network.name}
                              </div>
                            </td>
                            <td style={{ color: 'var(--ad-text-muted)', fontSize: 13 }}>
                              {n.network.name}
                            </td>
                            <td>
                              <span
                                style={{
                                  display: 'inline-block',
                                  fontSize: 11,
                                  fontWeight: 600,
                                  padding: '2px 10px',
                                  borderRadius: 999,
                                  letterSpacing: '0.02em',
                                  color: isLinked ? 'var(--ad-badge-green)' : 'var(--ad-badge-gray)',
                                  background: isLinked ? 'var(--ad-badge-green-bg)' : 'var(--ad-badge-gray-bg)',
                                  border: `1px solid ${isLinked ? 'var(--ad-badge-green-border)' : 'var(--ad-badge-gray-border)'}`,
                                }}
                              >
                                {isLinked ? t('political.linked') : t('political.unlinked')}
                              </span>
                            </td>
                            {!isController && (
                              <td>
                                {isLinked ? (
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    className="text-(--destructive)"
                                    loading={linkMutation.isPending}
                                    onClick={() => handleRevoke(publicId!)}
                                  >
                                    <X className="size-4" />
                                    {t('political.revoke')}
                                  </Button>
                                ) : (
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    loading={linkMutation.isPending}
                                    disabled={!n.advertiser}
                                    onClick={() => handleLink({
                                      system: 'ADNUNTIUS',
                                      id: n.advertiser!.publicId,
                                      name: n.advertiser!.name,
                                      data: {},
                                    })}
                                  >
                                    <Link className="size-4" />
                                    {t('political.link')}
                                  </Button>
                                )}
                              </td>
                            )}
                          </tr>
                        )
                      })}
                      {(campaign?.externalApprovals ?? [])
                        .filter((a) => !notifications.some((n) => n.advertiser?.publicId === a.id))
                        .map((a) => (
                          <tr key={a.id}>
                            <td style={{ paddingLeft: 20 }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <Building className="size-3.5" style={{ color: 'var(--ad-text-muted)' }} />
                                {a.name}
                              </div>
                            </td>
                            <td style={{ color: 'var(--ad-text-muted)', fontSize: 13 }}>
                              {a.system}
                            </td>
                            <td>
                              <span
                                style={{
                                  display: 'inline-block',
                                  fontSize: 11,
                                  fontWeight: 600,
                                  padding: '2px 10px',
                                  borderRadius: 999,
                                  letterSpacing: '0.02em',
                                  color: 'var(--ad-badge-green)',
                                  background: 'var(--ad-badge-green-bg)',
                                  border: '1px solid var(--ad-badge-green-border)',
                                }}
                              >
                                {t('political.linked')}
                              </span>
                            </td>
                            {!isController && (
                              <td>
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  className="text-(--destructive)"
                                  loading={linkMutation.isPending}
                                  onClick={() => handleRevoke(a.id)}
                                >
                                  <X className="size-4" />
                                  {t('political.revoke')}
                                </Button>
                              </td>
                            )}
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </CardContent>
              )}
            </Card>
          </div>
        )}

        {/* Save bar */}
        <div className={`detail-savebar${isDirty || isNew || mutation.isPending ? ' is-visible' : ''}`}>
          <div className="detail-savebar__left">
            {mutation.isPending && (
              <>
                <Loader2 className="size-4 animate-spin" />
                {t('common.saving')}
              </>
            )}
          </div>
          <div className="detail-savebar__actions">
            <Button
              type="button"
              variant="ghost"
              onClick={() => navigate('/political/campaigns')}
              disabled={mutation.isPending}
            >
              {t('common.cancel')}
            </Button>
            <Button
              type="submit"
              loading={mutation.isPending}
            >
              <Check className="size-4" />
              {isNew ? t('political.createCampaign') : t('common.save')}
            </Button>
          </div>
        </div>
      </form>

      <NewPayerModal
        open={payerModalOpen}
        onClose={() => { setPayerModalOpen(false); setPayerModalTopicIndex(null) }}
        onCreated={handlePayerCreated}
      />
      <UnsavedChangesDialog blocker={blocker} />
    </AppShell>
  )
}

// ── Outer page ──────────────────────────────────────────────────────────────

export function PoliticalCampaignEditPage() {
  const { t } = useTranslation()
  const { id } = useParams<{ id: string }>()
  const [searchParams] = useSearchParams()
  const isNew = searchParams.get('new') === 'true'
  const initialElectionId = searchParams.get('election') ?? ''
  const hasToken = AuthService.isAuthenticated()

  const { data: campaign, isLoading: loadingCampaign, isError } = useQuery({
    queryKey: ['politicalCampaign', id],
    queryFn: () => PoliticalCampaignsApi.getById(id!),
    enabled: !!id && hasToken && !isNew,
    retry: false,
  })

  const crumbsLoading = [
    { label: t('political.campaigns'), href: '/political/campaigns' },
    { label: isNew ? t('political.newCampaign') : t('political.editCampaign') },
  ]

  if (isError && !isNew) {
    return (
      <AppShell crumbs={crumbsLoading} showBack>
        <NetworkObjectLookup objectId={id!} />
      </AppShell>
    )
  }

  if (loadingCampaign && !isNew) {
    return (
      <AppShell crumbs={crumbsLoading} showBack>
        <FormSkeleton rows={2} columns={3} />
      </AppShell>
    )
  }

  return (
    <PoliticalCampaignEditForm
      key={id}
      campaign={campaign ?? null}
      id={id!}
      isNew={isNew}
      initialElectionId={initialElectionId}
    />
  )
}
